import { useMutation } from '@tanstack/react-query';

import {
  groupsService,
  CreateGroupPathRequest,
  ResolveGroupPathsResponse,
} from '@aslanpay/portal-common-lib';

export interface ResolveGroupPathsForCompanyRequest {
  companyCode: string;
  companyId: string;
  requests: CreateGroupPathRequest[];
}

export interface ResolveGroupPathsForCompanyResult {
  companyCode: string;
  result: ResolveGroupPathsResponse;
}

const resolveForCompany = async ({
  companyCode,
  companyId,
  requests,
}: ResolveGroupPathsForCompanyRequest): Promise<ResolveGroupPathsForCompanyResult> => {
  try {
    return { companyCode, result: await groupsService.resolveGroupPaths(companyId, requests) };
  } catch (error) {
    // A company's own group-batch call failing outright (network error, 500) must not lose the other
    // companies' results - synthesize a per-row failure for every org unit instead of letting it reject.
    return {
      companyCode,
      result: {
        results: requests.map((request) => ({
          id: request.id,
          error: {
            code: 'GROUP_PATH_RESOLUTION_FAILED',
            message: error instanceof Error ? error.message : 'Failed to resolve group paths',
          },
        })),
      },
    };
  }
};

/**
 * Resolves/creates each successfully-created company's org-unit group paths, one bulk call per company (the
 * endpoint is company-scoped). Applies every company independently - one company's group batch failing
 * outright never loses another company's results, and no company's own group failures are surfaced as a
 * mutation-level error - see resolveForCompany.
 */
const useResolveGroupPaths = () => {
  return useMutation<
    ResolveGroupPathsForCompanyResult[],
    Error,
    ResolveGroupPathsForCompanyRequest[]
  >((entries) => Promise.all(entries.map(resolveForCompany)));
};

export default useResolveGroupPaths;
